import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, fmtDate } from "@/components/PageHeader";
import Pipeline from "@/components/admin/Pipeline";
import RecordDetail from "@/components/admin/RecordDetail";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LayoutGrid, List, Plus, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function PipelineModule({ config }) {
  const { hasPerm } = useAuth();
  const canManage = hasPerm(`${config.module}:manage`);
  const [rows, setRows] = useState(null);
  const [view, setView] = useState("pipeline");
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    try { const { data } = await api.get(config.endpoint); setRows(data); }
    catch (e) { toast.error(apiError(e)); setRows([]); }
  }, [config.endpoint]);
  useEffect(() => { setRows(null); load(); }, [load]);

  const filtered = (rows || []).filter((r) => !search || JSON.stringify(r).toLowerCase().includes(search.toLowerCase()));

  const kpis = config.kpis.map((k) => ({ ...k, value: (rows || []).filter(k.filter).length }));

  const create = async () => {
    try { await api.post(config.endpoint, { ...form, status: form.status || config.statuses[0] }); toast.success("Created"); setCreateOpen(false); setForm({}); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid={`module-${config.path}`}>
      <PageHeader title={config.title} subtitle={config.subtitle}
        actions={
          <div className="flex items-center gap-2">
            <div className="flex rounded-md border border-border overflow-hidden">
              <button onClick={() => setView("pipeline")} className={`px-3 py-2 ${view === "pipeline" ? "bg-primary text-primary-foreground" : "bg-card"}`} data-testid="view-pipeline"><LayoutGrid className="w-4 h-4" /></button>
              <button onClick={() => setView("list")} className={`px-3 py-2 ${view === "list" ? "bg-primary text-primary-foreground" : "bg-card"}`} data-testid="view-list"><List className="w-4 h-4" /></button>
            </div>
            {canManage && <Button onClick={() => { setForm({}); setCreateOpen(true); }} className="rounded-md" data-testid={`${config.path}-add-btn`}><Plus className="w-4 h-4 mr-1.5" /> Add</Button>}
          </div>
        } />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {kpis.map((k) => (
          <div key={k.label} className="border border-border rounded-lg bg-card p-4" data-testid={`kpi-${k.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{k.label}</p>
            <p className="font-heading text-2xl font-bold mt-1">{k.value}</p>
          </div>
        ))}
      </div>

      <div className="relative mb-4 max-w-sm">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className="pl-9 bg-card" data-testid={`${config.path}-search`} />
      </div>

      {rows === null ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        : view === "pipeline" ? (
          <Pipeline items={filtered} statuses={config.statuses} onOpen={setActive} labelKey={config.labelKey} subKey={config.subKey} />
        ) : (
          <div className="border border-border rounded-lg bg-card overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow className="hover:bg-transparent">
                  {config.columns.map((c) => <TableHead key={c.key} className="text-xs uppercase tracking-wider">{c.label}</TableHead>)}
                </TableRow></TableHeader>
                <TableBody>
                  {filtered.map((r) => (
                    <TableRow key={r.id} className="cursor-pointer" onClick={() => setActive(r)} data-testid={`${config.path}-row`}>
                      {config.columns.map((c) => (
                        <TableCell key={c.key} className="py-2.5">
                          {c.type === "badge" ? <StatusBadge value={r[c.key]} /> : c.type === "date" ? <span className="text-muted-foreground">{fmtDate(r[c.key])}</span> : (r[c.key] || "—")}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                  {filtered.length === 0 && <TableRow><TableCell colSpan={99} className="text-center py-10 text-muted-foreground">No records.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

      <RecordDetail config={config} record={active} onClose={() => setActive(null)} onChanged={load} />

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New {config.title}</DialogTitle></DialogHeader>
          <div className="space-y-3.5">
            {config.createFields.map((f) => (
              <div key={f.key} className="space-y-1.5">
                <Label>{f.label}</Label>
                {f.type === "select" ? (
                  <Select value={form[f.key] || ""} onValueChange={(v) => setForm({ ...form, [f.key]: v })}>
                    <SelectTrigger data-testid={`create-${f.key}`}><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{(f.options || config.statuses).map((o) => <SelectItem key={o} value={o}>{o.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                  </Select>
                ) : <Input value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`create-${f.key}`} />}
              </div>
            ))}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button><Button onClick={create} data-testid={`${config.path}-create`}>Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
